import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { AnnexureService, API_BASE_URL } from '../../../../service/proxy';
import { AnnexureRangeSelectionComponent } from './steps/range-mapping/annexure-range-selection.component';
import { AnnexureColumnSelectionComponent } from './steps/column-mapping/annexure-column-selection.component';
import { AnnexureImportDataComponent } from './steps/import-data/annexure-import-data.component';
import { ErrorService } from '../../../shared/services/error-service';

import { WizardStep } from '../../../shared/controls/wizard-control/wizard-step.interface';
import { WizardStepComponent } from '../../../shared/controls/wizard-control/wizard-step.component';
import { WizardControlComponent } from '../../../shared/controls/wizard-control/wizard-control.component';
import { ToastService } from '../../../shared/toast/toast.service';
import { Inject, Optional } from '@angular/core';

@Component({
    selector: 'annexure-wizard',
    templateUrl: './annexure-wizard.component.html',
    styleUrl: './annexure-wizard.component.scss',

    imports: [
        CommonModule,
        FormsModule,

        WizardControlComponent,
        WizardStepComponent,

        AnnexureRangeSelectionComponent,
        AnnexureColumnSelectionComponent,
        AnnexureImportDataComponent,
    ],

    providers: [
        AnnexureService
    ]
})
export class AnnexureWizardComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('rowSelectionComponent')
    rowSelectionComponent!: WizardStep;

    @ViewChild('columnSelectionComponent')
    columnSelectionComponent!: WizardStep;

    @ViewChild('importComponent')
    importComponent!: WizardStep;

    public batchId: number = 0;
    public viewInitialized: boolean = false;
    public completing: boolean = false;

    private pollingTimer: any = null;
    private baseUrl: string;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private http: HttpClient,
        private annexureService: AnnexureService,
        private errorService: ErrorService,
        private toastService: ToastService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(API_BASE_URL) baseUrl?: string,
    ) {
        this.baseUrl = baseUrl ?? '';
    }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.batchId = +params['id'];
        });
    }

    ngAfterViewInit(): void {
        // Defer to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.viewInitialized = true;
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    isRowSelectionValid(): boolean {
         // Default valid until view ready
        if (!this.viewInitialized)
            return true;

        return this.rowSelectionComponent?.isValid() || false;
    }

    isColumnSelectionValid(): boolean {
        if (!this.viewInitialized)
            return true;

        return this.columnSelectionComponent?.isValid() || false;
    }

    isImportValid(): boolean {
        if (!this.viewInitialized) 
            return true;

        return this.importComponent?.isValid() || false;
    }

    finished(): void {
        this.completing = true;
        this.annexureService.completeAnnexureBatch(this.batchId)
            .subscribe({
                next: () => {
                    this.startPolling();
                },
                error: (error: any) => {
                    this.completing = false;
                    this.errorService.handleError(error);
                }
            }
        );
    }

    private startPolling(): void {
        this.pollingTimer = setInterval(() => {
            this.http.get<any>(`${this.baseUrl}/annexure/${this.batchId}/completion-status`)
                .subscribe({
                    next: (result) => {
                        if (result.status === 'completed') {
                            this.stopPolling();
                            this.completing = false;
                            this.toastService.show('Annexure batch completed successfully.', 'Success');
                            this.router.navigate([`/annexure`]);
                        } else if (result.status === 'failed') {
                            this.stopPolling();
                            this.completing = false;
                            this.toastService.show(`Completion failed: ${result.error}`, 'Error');
                        }
                    },
                    error: () => {
                        // Polling error — keep trying
                    }
                });
        }, 5000);
    }

    private stopPolling(): void {
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    onCancel(): void {
        this.router.navigate([`/annexure`]);
    }
}